import React from "react";
import { RotateCcw } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

// Hooks and utilities
import { useGameLogic } from "./hooks/useGameLogic";
import { calculateDisplayValues } from "./utils/displayValues";
import { convertToScorecardData } from "./utils/scorecardData";
import {
  staggerContainer,
  fadeInUp,
  getGameTitle,
} from "./constants/game";

// Components
import {
  StatusBar,
  TopNavigation,
  ChaseTargetCard,
  LastManStandsCard,
  EditTeamDrawer,
  EditPlayerDrawer,
  ScoringKeypad,
} from "./components/GameComponents";

import {
  ScorePanel,
  PlayerPanel,
  OverTracker,
} from "./components/GameDisplayComponents";

import {
  RetirementNotification,
  MaxBallsOverNotification,
} from "./components/NotificationComponents";

import { MatchSettings } from "./components/MatchSettings";
import { WicketRecording } from "./components/WicketRecording";
import { GamesScreen } from "./components/GamesScreen";
import { Scorecard } from "./components/Scorecard";
import { TeamsPlayersScreen } from "./components/TeamsPlayersScreen";
import { HomeScreen } from "./components/HomeScreen";
import { BottomNavigation } from "./components/BottomNavigation";

export default function App() {
  const {
    // State
    gameState,
    gameTemplates,
    gameHistoryRecords,
    savedTeams,
    favoriteTemplates,
    currentScreen,
    editDialog,
    editTeamDialog,
    showSettings,
    showWicketRecording,
    showGamesScreen,
    showScorecard,
    showTeamsPlayersScreen,
    pendingRetirement,
    pendingMaxBallsOver,
    canUndo,
    canRedo,

    // State setters
    setShowSettings,
    setShowWicketRecording,
    setShowGamesScreen,
    setShowScorecard,
    setShowTeamsPlayersScreen,
    setEditDialog,
    setEditTeamDialog,
    setPendingRetirement,
    setPendingMaxBallsOver,

    // Getters
    getCurrentInnings,
    getBattingTeamName,
    getTeamPlayersForEdit,

    // Handlers
    handleScore,
    handleWicketConfirm,
    handlePlayerEdit,
    handlePlayerNameSave,
    handleAddNewPlayer,
    handleTeamEdit,
    handleTeamNameSave,
    handlePlayerSwitch,
    handleSettingsSave,
    handleUpdateTeamPlayers,
    handleSaveTemplate,
    handleEditTemplate,
    handleToggleFavorite,
    handleEndGame,
    handleEndInnings,
    handleStartNewGame,
    handleRetirement,
    handleMaxBallsOver,
    handleOverrideRetirement,
    handleOverrideMaxBallsOver,
    handleSaveTeam,
    handleDeleteTeam,
    handleUpdateTeam,
    handleNavigateToGames,
    handleNavigateToTeams,
    handleNavigateToHome,

    // Template functions
    setGameTemplates,
    setGameHistoryRecords,
  } = useGameLogic();

  // Calculate display values
  const displayValues = calculateDisplayValues(gameState);
  const currentInnings = getCurrentInnings();
  const gameTitle = getGameTitle(gameState);
  const scorecardData = convertToScorecardData(gameState);

  // Show navigation screens
  if (
    currentScreen === "home" &&
    !showGamesScreen &&
    !showTeamsPlayersScreen
  ) {
    return (
      <>
        <HomeScreen
          gameTemplates={gameTemplates}
          favoriteTemplates={favoriteTemplates}
          onStartNewGame={handleStartNewGame}
          onToggleFavorite={handleToggleFavorite}
          onNavigateToGames={handleNavigateToGames}
        />
        <BottomNavigation
          currentScreen={currentScreen}
          onNavigateToHome={handleNavigateToHome}
          onNavigateToGames={handleNavigateToGames}
          onNavigateToTeams={handleNavigateToTeams}
        />
      </>
    );
  }

  if (showGamesScreen) {
    return (
      <>
        <GamesScreen
          isOpen={showGamesScreen}
          onClose={() => setShowGamesScreen(false)}
          onStartNewGame={handleStartNewGame}
          gameTemplates={gameTemplates}
          gameHistory={gameHistoryRecords}
          favoriteTemplates={favoriteTemplates}
          onDeleteTemplate={(id) =>
            setGameTemplates((prev) =>
              prev.filter((t) => t.id !== id),
            )
          }
          onDeleteGame={(id) =>
            setGameHistoryRecords((prev) =>
              prev.filter((g) => g.id !== id),
            )
          }
          onToggleFavorite={handleToggleFavorite}
          onEditTemplate={handleEditTemplate}
        />
        <BottomNavigation
          currentScreen={currentScreen}
          onNavigateToHome={handleNavigateToHome}
          onNavigateToGames={handleNavigateToGames}
          onNavigateToTeams={handleNavigateToTeams}
        />
      </>
    );
  }

  if (showTeamsPlayersScreen) {
    return (
      <>
        <TeamsPlayersScreen
          isOpen={showTeamsPlayersScreen}
          onClose={() => setShowTeamsPlayersScreen(false)}
          savedTeams={savedTeams}
          onSaveTeam={handleSaveTeam}
          onDeleteTeam={handleDeleteTeam}
          onUpdateTeam={handleUpdateTeam}
        />
        <BottomNavigation
          currentScreen={currentScreen}
          onNavigateToHome={handleNavigateToHome}
          onNavigateToGames={handleNavigateToGames}
          onNavigateToTeams={handleNavigateToTeams}
        />
      </>
    );
  }

  // Active game screen
  return (
    <div className="bg-white min-h-screen flex flex-col w-full max-w-[800px] mx-auto relative">
      {/* Top Navigation */}
      <motion.div
        className="sticky top-0 bg-white z-40 border-b border-gray-100"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex flex-col gap-2 p-4 pb-3">
          <StatusBar />
          <TopNavigation
            teamA={gameState.teamA}
            teamB={gameState.teamB}
            gameTitle={gameTitle}
            onEditTeam={handleTeamEdit}
            onOpenSettings={() => setShowSettings(true)}
            onOpenGames={handleNavigateToGames}
            onOpenScorecard={() => setShowScorecard(true)}
            oversPerInnings={
              gameState.matchSettings.oversPerInnings
            }
            numberOfPlayers={
              gameState.matchSettings.numberOfPlayers
            }
          />
        </div>
      </motion.div>

      {/* Main Content - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        <motion.div
          className="flex flex-col gap-4 sm:gap-6 p-4"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          {/* Score Panel */}
          <ScorePanel
            overs={displayValues.oversDisplay}
            innings={`Innings ${gameState.currentInnings}`}
            wicketsRemaining={displayValues.wicketsRemaining}
            runRate={displayValues.runRate}
            projected={displayValues.projected}
            currentScore={displayValues.currentScore}
            battingTeam={getBattingTeamName()}
            isSecondInnings={displayValues.isSecondInnings}
          />

          {/* Chase Target Card */}
          <AnimatePresence>
            {displayValues.showChaseTarget &&
              displayValues.targetRuns > 0 && (
                <ChaseTargetCard
                  targetRuns={displayValues.targetRuns}
                  ballsRemaining={displayValues.ballsRemaining}
                  runRate={displayValues.requiredRunRate}
                  teamName={getBattingTeamName()}
                />
              )}
          </AnimatePresence>

          {/* Last Man Stands Card */}
          <AnimatePresence>
            {displayValues.showLastManStands && (
              <LastManStandsCard
                batterName={displayValues.lastManBatterName}
                teamName={getBattingTeamName()}
              />
            )}
          </AnimatePresence>

          {/* Player Panels */}
          <motion.div
            className="flex flex-row items-center justify-center gap-2 w-full"
            variants={fadeInUp}
          >
            <div className="flex-1">
              <PlayerPanel
                name={currentInnings.batter1.name}
                score={currentInnings.batter1.runs}
                balls={currentInnings.batter1.balls}
                isOnStrike={currentInnings.batter1.onStrike}
                isRetired={currentInnings.batter1.isRetired}
                isLastManStands={
                  currentInnings.isLastManStands &&
                  currentInnings.batter1.name ===
                    "Last Man Stands"
                }
                onEdit={() => handlePlayerEdit("batter1")}
              />
            </div>
            <div className="w-[44px] flex items-center justify-center">
              <motion.button
                onClick={handlePlayerSwitch}
                className="bg-white rounded-full p-2 shadow-sm transform rotate-90 hover:bg-gray-50 transition-colors"
                disabled={
                  gameState.gameComplete ||
                  currentInnings.isLastManStands
                }
                whileHover={{ scale: 1.05, rotate: 95 }}
                whileTap={{ scale: 0.95, rotate: 85 }}
                transition={{
                  type: "spring",
                  stiffness: 400,
                  damping: 17,
                }}
              >
                <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                  <RotateCcw className="w-4 h-4 text-gray-600" />
                </div>
              </motion.button>
            </div>
            <div className="flex-1">
              <PlayerPanel
                name={currentInnings.batter2.name}
                score={currentInnings.batter2.runs}
                balls={currentInnings.batter2.balls}
                isOnStrike={currentInnings.batter2.onStrike}
                isRetired={currentInnings.batter2.isRetired}
                isLastManStands={
                  currentInnings.isLastManStands &&
                  currentInnings.batter2.name ===
                    "Last Man Stands"
                }
                onEdit={() => handlePlayerEdit("batter2")}
              />
            </div>
          </motion.div>

          {/* Bowler Panel */}
          <PlayerPanel
            name={currentInnings.bowler.name}
            score={currentInnings.bowler.runsConceded}
            balls={currentInnings.bowler.ballsBowled}
            wickets={currentInnings.bowler.wicketsTaken}
            isOnStrike={false}
            onEdit={() => handlePlayerEdit("bowler")}
            variant="bowler"
          />

          {/* Game Complete Message */}
          <AnimatePresence>
            {gameState.gameComplete && (
              <motion.div
                className="bg-green-50 border border-green-200 rounded-lg p-4 w-full"
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -20 }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 25,
                }}
              >
                <motion.h3
                  className="font-semibold text-green-800 text-center"
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  transition={{
                    delay: 0.1,
                    type: "spring",
                    stiffness: 400,
                  }}
                >
                  Game Complete!
                </motion.h3>
                {gameState.winner && (
                  <motion.p
                    className="text-green-700 text-center mt-1"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    {gameState.winner} won the match
                  </motion.p>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Over Tracker - Outside main gap container */}
        <div className="px-4 pb-2">
          <OverTracker
            currentOverBalls={currentInnings.currentOverBalls}
            previousOvers={currentInnings.previousOvers}
            currentOverNumber={currentInnings.currentOver}
            ballsPerOver={gameState.matchSettings.ballsPerOver}
            maxBallsPerOver={
              gameState.matchSettings.customRules
                .maxBallsPerOver
            }
            isMaxBallsEnabled={
              gameState.matchSettings.customRules
                .enableMaxBallsPerOver
            }
          />
        </div>

        {/* Scoring Keypad - Minimal gap */}
        <div className="px-4">
          <ScoringKeypad
            onScore={handleScore}
            canUndo={canUndo}
            canRedo={canRedo}
            gameComplete={gameState.gameComplete}
          />
        </div>
      </div>

      {/* Bottom padding for navigation */}
      <div className="h-20 bg-white" />

      {/* Bottom Navigation */}
      <BottomNavigation
        currentScreen="games"
        onNavigateToHome={handleNavigateToHome}
        onNavigateToGames={handleNavigateToGames}
        onNavigateToTeams={handleNavigateToTeams}
      />

      {/* Edit Player Drawer */}
      <EditPlayerDrawer
        isOpen={editDialog.isOpen}
        onClose={() =>
          setEditDialog({
            isOpen: false,
            playerType: "",
            currentName: "",
          })
        }
        playerType={editDialog.playerType}
        currentName={editDialog.currentName}
        onSave={handlePlayerNameSave}
        teamPlayers={getTeamPlayersForEdit()}
        onAddNewPlayer={handleAddNewPlayer}
      />

      {/* Edit Team Drawer */}
      <EditTeamDrawer
        isOpen={editTeamDialog.isOpen}
        onClose={() =>
          setEditTeamDialog({
            isOpen: false,
            teamLetter: "",
            currentName: "",
          })
        }
        teamLetter={editTeamDialog.teamLetter}
        currentName={editTeamDialog.currentName}
        onSave={handleTeamNameSave}
      />

      {/* Match Settings */}
      <MatchSettings
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        settings={gameState.matchSettings}
        onSave={handleSettingsSave}
        onSaveTemplate={handleSaveTemplate}
        onEndGame={handleEndGame}
        onEndInnings={handleEndInnings}
        teamAPlayers={gameState.teamAPlayers}
        teamBPlayers={gameState.teamBPlayers}
        onUpdateTeamPlayers={handleUpdateTeamPlayers}
        savedTeams={savedTeams}
        onSaveTeam={handleSaveTeam}
        onOpenTeamsPage={handleNavigateToTeams}
      />

      {/* Wicket Recording */}
      <WicketRecording
        isOpen={showWicketRecording}
        onClose={() => setShowWicketRecording(false)}
        onConfirm={handleWicketConfirm}
        currentBatter1={currentInnings.batter1}
        currentBatter2={currentInnings.batter2}
        currentBowler={currentInnings.bowler.name}
        battingTeamPlayers={
          gameState.battingTeam === "A"
            ? gameState.teamAPlayers
            : gameState.teamBPlayers
        }
        bowlingTeamPlayers={
          gameState.bowlingTeam === "A"
            ? gameState.teamAPlayers
            : gameState.teamBPlayers
        }
      />

      {/* Scorecard */}
      <Scorecard
        isOpen={showScorecard}
        onClose={() => setShowScorecard(false)}
        teamA={gameState.teamA}
        teamB={gameState.teamB}
        innings1={scorecardData.innings1}
        innings2={scorecardData.innings2}
        currentInnings={gameState.currentInnings}
        matchSettings={gameState.matchSettings}
        gameComplete={gameState.gameComplete}
        winner={gameState.winner}
      />

      {/* Retirement Notification */}
      {pendingRetirement && (
        <RetirementNotification
          playerName={
            pendingRetirement.player === "batter1"
              ? currentInnings.batter1.name
              : currentInnings.batter2.name
          }
          runs={pendingRetirement.runs}
          retirementRuns={
            gameState.matchSettings.customRules.retirementRuns
          }
          onConfirm={handleRetirement}
          onCancel={() => setPendingRetirement(null)}
          onOverride={handleOverrideRetirement}
        />
      )}

      {/* Max Balls Over Notification */}
      {pendingMaxBallsOver && (
        <MaxBallsOverNotification
          overNumber={pendingMaxBallsOver.overNumber}
          ballsInOver={pendingMaxBallsOver.ballsInOver}
          maxBalls={pendingMaxBallsOver.maxBalls}
          isLastOver={pendingMaxBallsOver.isLastOver}
          onConfirm={handleMaxBallsOver}
          onCancel={() => setPendingMaxBallsOver(null)}
          onOverride={handleOverrideMaxBallsOver}
        />
      )}
    </div>
  );
}